import { useState } from 'react';
import Script from 'next/script';

import type { IAffiliateHero20Fields } from '@/types/contentful';

import { usePageDataContext } from '~/context/PageData';
import { usePublicRuntimeConfig } from '~/hooks/usePublicRuntimeConfig';

import MobileView from './Viewports/MobileView';
import DesktopView from './Viewports/DesktopView';
import SplitHero from '~/components/FullwidthHero/SplitHero';
import { AffiliateLogoGrouping } from './AffiliateLogoGrouping';

import { GreenlightThemeOptions } from '~/theme/themes/greenlight';

const AppsflyerQrCodeContainerId = 'appsflyer-qr-code-container';

export type AffiliateHero2Props = IAffiliateHero20Fields;

export enum greenlightBrandingEnum {
  GREENLIGHT_ICON = 'Greenlight Icon',
  GREENLIGHT_LOGO = 'Greenlight Logo',
  NONE = 'None'
}

export function AffiliateHero2({
  useEmbeddedSmartLink,
  useAltLayout,
  ...props
}: AffiliateHero2Props) {
  const [resultUrl, setResultUrl] = useState();

  const {
    query: { token: partnerToken }
  } = usePageDataContext();
  const { libertyOneLinkUrl } = usePublicRuntimeConfig();

  const handleOnLoad = () => {
    //Initializing Smart Script arguments
    const deepLinkURI = 'greenlightme://partner-enablement/affiliation';

    // If a media source key is NOT FOUND on the link and NO default value is found, the script will return a null string
    const mediaSource = {
      keys: ['inmedia'],
      defaultValue: 'my_media_default_source'
    };
    const campaign = { keys: ['incmp'] };
    const deepLinkValue = {
      keys: ['page_name'],
      defaultValue: encodeURIComponent(`partner-enablement/affiliation`)
    };

    //Function is embedded on the window object in a global parameter called window.AF_SMART_SCRIPT.
    //Onelink URL is generated
    const encodedDeepLink = encodeURIComponent(deepLinkURI);
    const result = window.AF_SMART_SCRIPT.generateOneLinkURL({
      oneLinkURL: libertyOneLinkUrl,
      afParameters: {
        mediaSource: mediaSource,
        campaign: campaign,
        deepLinkValue: deepLinkValue,
        afCustom: [
          {
            paramKey: 'af_dp',
            defaultValue: `${encodedDeepLink}?token=${partnerToken}`
          },
          { paramKey: 'token', defaultValue: partnerToken }
        ]
      }
    });

    if (result) {
      setResultUrl(result.clickURL);
      const qrOptions = {
        codeColor: GreenlightThemeOptions.COLORS.GREEN
      };
      window.AF_SMART_SCRIPT.displayQrCode(
        AppsflyerQrCodeContainerId,
        qrOptions
      );
    }
  };

  return (
    <>
      {useAltLayout ? (
        <>
          <SplitHero
            {...(props as any)}
            theme={'default'}
            affiliateHero={true}
            leftPaneHeader={
              props.affiliateLogo ? (
                <div className="[&>div]:mx-0 [&>div]:min-h-6 [&>div]:w-fit">
                  <AffiliateLogoGrouping
                    useAltLayout={true}
                    affiliateLogo={props.affiliateLogo as any}
                    useGreenlightIcon={props.useGreenlightIcon}
                    greenlightBranding={props.greenlightBranding as any}
                  />
                </div>
              ) : undefined
            }
          />
        </>
      ) : (
        <>
          <MobileView
            {...props}
            useEmbeddedSmartLink={useEmbeddedSmartLink}
            appsFlyerOverride={resultUrl}
          />
          <DesktopView
            {...props}
            useEmbeddedSmartLink={useEmbeddedSmartLink}
            qrContainerId={AppsflyerQrCodeContainerId}
          />
        </>
      )}

      {useEmbeddedSmartLink && (
        <Script
          data-testid="appsflyer-smart-script"
          type="text/javascript"
          src="https://onelinksmartscript.appsflyer.com/onelink-smart-script-latest.js"
          strategy="afterInteractive"
          onLoad={handleOnLoad}
        />
      )}
    </>
  );
}
